/**
 * The durable record of what this machine has changed and the server has not
 * yet been told (§11).
 *
 * Two rules hold this together, and everything else here follows from them.
 *
 * *A mutation and its local effect are one write.* `enqueueMutation` takes no
 * transaction of its own and is meant to be called from inside the caller's —
 * see `repositories/commit.ts`. A mutation without its effect is a change the
 * operator cannot see but the server will apply; an effect without its mutation
 * is a change the operator can see and the server will never hear about. The
 * second is the one that loses a shift's work, and it is the one an
 * "enqueue after saving" ordering produces on a browser crash.
 *
 * *The count is derived, never held.* §19.2 requires the unsynced figure to come
 * from durable state, because the failure it guards against is a counter that
 * says zero after a restart while the outbox is full. Every count here is a
 * query.
 */
import {
  offlineDatabase,
  type MutationOperation,
  type MutationState,
  type OfflineDatabase,
  type StoredMutation
} from "../db/schema"
import type { OutboxIdentity } from "./identity"

/** Everything about a mutation that is not the outbox's to decide. */
export interface MutationDraft {
  entryUuid: string
  actingUserUuid: string
  actingRoleUuid: string | null
  stationUuid: string
  stationCode: string
  registerCode: string
  operation: MutationOperation
  changedPaths: string[]
  baseRevision: number
  values: Record<string, unknown>
  /** What the server last confirmed at those paths, for §13.1's Base column. */
  baseValues: Record<string, unknown>
  /** Which row each touched index means, so a reorder cannot misalign a merge. */
  rowUids: Record<string, string>
  occurredAt: string
  authorizationSnapshotVersion: number | null
  schemaVersion: number | null
  schemaDigest: string | null
  dependsOn: string[]
  conflictUuid: string | null
  domainResolution: string | null
  resolutionNote: string | null
  /**
   * Which section of a section-partitioned register a create was written on.
   *
   * Recorded on the mutation rather than derived on arrival because the server
   * cannot derive it: the page that chose the section is long gone by the time
   * an outbox drains, and `lc_pn`'s crossings are generated *per section*, so a
   * create applied without one is a document with no crossings in it.
   */
  lcSectionId: string | null
}

/**
 * States a mutation is in while the server still owes an answer. `rejected` is
 * excluded deliberately: it is terminal and needs a human, so counting it as
 * "awaiting synchronization" would tell an operator to keep waiting for
 * something that will never arrive (§12.4).
 */
export const IN_FLIGHT: MutationState[] = [ "pending", "syncing" ]

/**
 * Write the mutation. **Call inside the transaction that writes its effect.**
 *
 * `deviceSeq` is passed in rather than allocated here so that the allocation is
 * visibly part of the caller's transaction too — see `allocateDeviceSeq`.
 */
export async function enqueueMutation(
  draft: MutationDraft,
  identity: OutboxIdentity,
  deviceSeq: number,
  db: OfflineDatabase = offlineDatabase(),
  nowMs: number = Date.now()
): Promise<StoredMutation> {
  const mutation: StoredMutation = {
    ...draft,
    mutationUuid: crypto.randomUUID(),
    stationDeviceUuid: identity.deviceUuid,
    deviceSeq,
    state: "pending",
    attemptCount: 0,
    lastError: null,
    createdAt: new Date(nowMs).toISOString()
  }

  await db.mutations.add(mutation)

  return mutation
}

/** In causal order, which is what a push has to send them in (§12.1). */
export async function pendingMutations(
  db: OfflineDatabase = offlineDatabase(),
  limit?: number
): Promise<StoredMutation[]> {
  const mutations = await db.mutations.where("state").equals("pending").sortBy("deviceSeq")

  return limit === undefined ? mutations : mutations.slice(0, limit)
}

/** What §19.2's indicator shows: changes this machine is still holding. */
export async function unsyncedCount(db: OfflineDatabase = offlineDatabase()): Promise<number> {
  return db.mutations.where("state").anyOf(IN_FLIGHT).count()
}

/** Mutations the server refused outright. Terminal, and surfaced rather than retried. */
export async function rejectedCount(db: OfflineDatabase = offlineDatabase()): Promise<number> {
  return db.mutations.where("state").equals("rejected").count()
}

/**
 * Phase 8 fills this table; Phase 6 counts it, because §19.2's indicator has a
 * conflict state and a count that reads a table which does not exist is a count
 * that has to be faked.
 */
export async function unresolvedConflictCount(db: OfflineDatabase = offlineDatabase()): Promise<number> {
  return db.conflicts.where("resolved").equals(0).count()
}

export async function mutationsForEntry(
  entryUuid: string,
  db: OfflineDatabase = offlineDatabase()
): Promise<StoredMutation[]> {
  return db.mutations.where("entryUuid").equals(entryUuid).sortBy("deviceSeq")
}

/** Whether any mutation touching this entry is still owed an answer. */
export async function entryIsUnsynced(
  entryUuid: string,
  db: OfflineDatabase = offlineDatabase()
): Promise<boolean> {
  const mutations = await db.mutations.where("entryUuid").equals(entryUuid).toArray()

  return mutations.some(mutation => IN_FLIGHT.includes(mutation.state))
}

export async function markMutationState(
  mutationUuid: string,
  state: MutationState,
  changes: Partial<Pick<StoredMutation, "attemptCount" | "lastError">> = {},
  db: OfflineDatabase = offlineDatabase()
): Promise<void> {
  await db.mutations.update(mutationUuid, { state, ...changes })
}
